import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-community';

import type {
  MergeRowDto,
  RowDecisionStatus,
} from '../api/mergeResolution.types';

interface DecisionSwitchProps {
  status: RowDecisionStatus;
  disabled: boolean;
  onTake: (choice: 'MINE' | 'THEIRS') => void;
  onUndo: () => void;
  ariaLabel: string;
}

// Sized to fit the 38px grid row. An explicit height, because the grid's cell
// stretches a bare button group to the full row height; centred in the row
// by the group's own `alignSelf`.
const COMPACT_BUTTON = {
  height: 22,
  minHeight: 0,
  py: 0,
  lineHeight: 1,
  fontSize: '0.75rem',
};

const DecisionSwitch = ({
  status,
  disabled,
  onTake,
  onUndo,
  ariaLabel,
}: DecisionSwitchProps) => {
  const value = (() => {
    if (status === 'CHOSE_MINE') return 'MINE';
    if (status === 'CHOSE_THEIRS') return 'THEIRS';
    return null;
  })();

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: 'MINE' | 'THEIRS' | null
  ) => {
    if (newValue === null) {
      onUndo();
    } else {
      onTake(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      disabled={disabled}
      size="small"
      sx={{ alignSelf: 'center' }}
      aria-label={ariaLabel}
    >
      <ToggleButton value="MINE" sx={COMPACT_BUTTON}>
        Mine
      </ToggleButton>
      <ToggleButton value="THEIRS" sx={COMPACT_BUTTON}>
        Theirs
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default DecisionSwitch;

// AG Grid's `params.context` is ONE object shared by the whole grid, so it
// cannot carry a per-row `status`. The context supplies row-keyed accessors
// and the renderer resolves them from `params.data.Input_RowID`, matching the
// `statusFor(rowId)` shape Task 32's column builder already uses.
interface DecisionSwitchContext {
  statusFor: (rowId: string) => RowDecisionStatus;
  onTake: (rowId: string, choice: 'MINE' | 'THEIRS') => void;
  onUndo: (rowId: string) => void;
  disabled?: boolean;
}

/**
 * Keeps presses on its children away from the grid, so clicking a decision
 * button does not also select or focus the cell it sits in.
 *
 * Native listeners, not React props: the grid listens on its own DOM nodes,
 * which run before React's root-level handlers, so a React `onMouseDown`
 * would stop the event too late. The click itself is untouched.
 */
const GridMouseShield = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const stop = (event: Event) => event.stopPropagation();
    el.addEventListener('mousedown', stop);
    el.addEventListener('touchstart', stop);
    return () => {
      el.removeEventListener('mousedown', stop);
      el.removeEventListener('touchstart', stop);
    };
  }, []);
  return (
    // The grid cell is not a flex container, so a bare inline group would sit
    // on the text baseline instead of the middle of the row.
    <Box
      ref={ref}
      sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
    >
      {children}
    </Box>
  );
};

export const DecisionSwitchRenderer = (
  params: ICellRendererParams<MergeRowDto, unknown, unknown>
) => {
  const context = params.context as DecisionSwitchContext;
  const row = params.data;
  if (!row) return null;

  const rowId = row.Input_RowID;

  return (
    <GridMouseShield>
      <DecisionSwitch
        status={context.statusFor(rowId)}
        disabled={context.disabled ?? false}
        onTake={(choice) => context.onTake(rowId, choice)}
        onUndo={() => context.onUndo(rowId)}
        ariaLabel={`Decision for ${row.Dimensions.join(' / ')}`}
      />
    </GridMouseShield>
  );
};
